import {
    Box, styled, Typography, Popper, Paper, Fade,
} from '@mui/material';
import { useState } from 'react';
import { LoadingStateImage } from './LoadingStateImage';

type AssetsImagesProps = {
    assetsPreviewUrl?: string;
    assetsPrintUrl?: string;
    assetsOriginalUrl?: string;
};

const StyledGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(2),
    width: '100%',
    maxWidth: '100%',
}));

const ENLARGED_SIZE = 400;

export const AssetsImages = ({
    assetsPreviewUrl,
    assetsPrintUrl,
    assetsOriginalUrl,
}: AssetsImagesProps): JSX.Element => {
    const items = [
        { label: 'Preview', src: assetsPreviewUrl },
        { label: 'Print', src: assetsPrintUrl },
        { label: 'Original', src: assetsOriginalUrl },
    ];

    const hasAnyAsset = items.some((i) => !!i.src);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleMouseEnter = (targetElement: HTMLElement, src?: string): void => {
        if (!src) {
            return;
        }
        setAnchorEl(targetElement);
    };
    const handleMouseLeave = (): void => {
        setAnchorEl(null);
    };

    return (
        <Box>
            {hasAnyAsset ? (
                <StyledGrid>
                    {items.map((item) => (
                        <Box key={item.label} sx={{ p: 2 }}>
                            <Typography
                                color="text.secondary"
                                sx={{ mb: 1, display: 'block' }}
                                variant="caption"
                            >
                                {item.label}
                            </Typography>
                            <Box
                                sx={{ cursor: item.src ? 'zoom-in' : 'default' }}
                                onMouseEnter={(e): void => handleMouseEnter(e.currentTarget as HTMLElement, item.src)}
                                onMouseLeave={handleMouseLeave}
                            >
                                <LoadingStateImage
                                    skeletonHeight={150}
                                    skeletonWidth={200}
                                    src={item.src}
                                />
                            </Box>
                            <Popper
                                transition
                                anchorEl={anchorEl}
                                modifiers={[
                                    { name: 'offset', options: { offset: [0, 10] } },
                                    { name: 'preventOverflow', options: { altAxis: true, tether: true, padding: 16 } },
                                ]}
                                open={open}
                                placement="right-start"
                                sx={{ zIndex: 1500 }}
                            >
                                {({ TransitionProps }): JSX.Element => (
                                    <Fade {...TransitionProps} timeout={200}>
                                        <Paper
                                            elevation={8}
                                            sx={{
                                                p: 1,
                                                borderRadius: 1,
                                                maxWidth: ENLARGED_SIZE + 16,
                                                maxHeight: ENLARGED_SIZE + 16,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={item.src}
                                                sx={{
                                                    width: ENLARGED_SIZE,
                                                    height: ENLARGED_SIZE,
                                                    objectFit: 'contain',
                                                    display: 'block',
                                                }}
                                            />
                                        </Paper>
                                    </Fade>
                                )}
                            </Popper>
                        </Box>
                    ))}
                </StyledGrid>
            ) : (
                <Box
                    sx={{
                        p: 2,
                        textAlign: 'center',
                        color: 'text.secondary',
                        maxWidth: '100%',
                    }}
                >
                    No images to show.
                </Box>
            )}
        </Box>
    );
};
